import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckIcon from "@mui/icons-material/Check";
import MessageIcon from "@mui/icons-material/Message";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import {
  MARKETPLACE_RESULT_ACTION_LABELS,
  MarketplaceResultAction,
  MarketplaceResultActionPlan,
  planMarketplaceResultActions,
} from "marketplace/_views/marketplace-result-actions";
import { marketplaceResultIsPhone } from "marketplace/_views/marketplace-result-card-layout";
import SendResultToContactDialog from "marketplace/_views/send-result-to-contact-dialog";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import useSessionUser from "permissions/session/use-session-user";
import React from "react";
import NetworkInvitationForm from "users/networking/connections/_views/components/network-invitation-form";
import useNetworkConnection, {
  NetworkConnection,
} from "users/networking/connections/_views/components/use-network-connection";
import { NetworkStatus } from "users/networking/connections/network-status";

type MarketplaceResultFooterProps = {
  /** The result's name, so the ⋮ button says whose actions it holds. */
  resultName: string;
  /** Where the result's profile lives; Copy link puts this address on the clipboard. */
  profilePath: string;
  /** The person the viewer connects with: the individual, the company's contact, the team's leader. */
  networkUserId?: Guid;
  onCreateProposal?: () => void;
  onSendMessage?: () => void;
};

/**
 * A result card's footer: at most two buttons and a ⋮ menu, with the one that leads contained and on
 * the right, and where the viewer stands with the result in quiet text on the left. Which action goes
 * where is `planMarketplaceResultActions`; this only draws it.
 */
export default function MarketplaceResultFooter(props: Readonly<MarketplaceResultFooterProps>) {
  if (props.networkUserId) return <NetworkedFooter {...props} networkUserId={props.networkUserId} />;
  return <FooterLayout {...props} />;
}

function NetworkedFooter(props: Readonly<MarketplaceResultFooterProps & { networkUserId: Guid }>) {
  const connection = useNetworkConnection(props.networkUserId);
  return <FooterLayout {...props} connection={connection.isAvailable ? connection : undefined} />;
}

type FooterLayoutProps = MarketplaceResultFooterProps & { connection?: NetworkConnection };

function FooterLayout(props: Readonly<FooterLayoutProps>) {
  const { resultName, profilePath, connection, onCreateProposal, onSendMessage } = props;

  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [invitationAnchor, setInvitationAnchor] = React.useState<HTMLElement | null>(null);
  const [invitationMessage, setInvitationMessage] = React.useState<string>("");
  const [isSendingToContact, setIsSendingToContact] = React.useState<boolean>(false);
  const sessionUser = useSessionUser();
  const profileUrl = `${globalThis.location.origin}${profilePath}`;

  const plan: MarketplaceResultActionPlan = planMarketplaceResultActions({
    canCreateProposal: Boolean(onCreateProposal),
    canMessage: Boolean(onSendMessage),
    network: connection?.status,
    canSendToContact: sessionUser?.canAccessNetwork() === true,
  });

  async function sendInvitation() {
    if (!connection) return;
    const sent = await connection.sendInvitation(invitationMessage);
    if (!sent) return;
    setInvitationAnchor(null);
    setInvitationMessage("");
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(profileUrl);
      enqueueSnackbar("Link copied to clipboard", { variant: "info" });
    } catch {
      enqueueSnackbar("Couldn't copy link to clipboard", { variant: "error" });
    }
  }

  function perform(action: MarketplaceResultAction, trigger: HTMLElement) {
    switch (action) {
      case MarketplaceResultAction.CreateProposal:
        return onCreateProposal?.();
      case MarketplaceResultAction.SendMessage:
        return onSendMessage?.();
      case MarketplaceResultAction.AddToNetwork:
        return setInvitationAnchor(trigger);
      case MarketplaceResultAction.AcceptInvitation:
        return void connection?.acceptInvitation();
      case MarketplaceResultAction.CancelInvitation:
        return void connection?.cancelInvitation();
      case MarketplaceResultAction.RejectInvitation:
        return void connection?.rejectInvitation();
      case MarketplaceResultAction.RemoveFromNetwork:
        return void connection?.removeConnection();
      case MarketplaceResultAction.CopyLink:
        return void copyLink();
      case MarketplaceResultAction.SendToContact:
        return setIsSendingToContact(true);
    }
  }

  function renderButton(action: MarketplaceResultAction, variant: "contained" | "outlined", className: string) {
    return (
      <Button
        className={className}
        variant={variant}
        color="primary"
        disableElevation
        startIcon={ACTION_ICONS[action]}
        disabled={isNetworkAction(action) && connection?.isSaving}
        onClick={(event) => perform(action, event.currentTarget)}
      >
        {MARKETPLACE_RESULT_ACTION_LABELS[action]}
      </Button>
    );
  }

  return (
    <FooterRow>
      {plan.connectionStatus && (
        <ConnectionStatus className="status">
          {connection?.status === NetworkStatus.InNetwork && <CheckIcon fontSize="inherit" />}
          {plan.connectionStatus}
        </ConnectionStatus>
      )}
      {plan.secondary && renderButton(plan.secondary, "outlined", "secondary")}
      {plan.primary && renderButton(plan.primary, "contained", "primary")}
      <IconButton
        className="more"
        aria-label={`More actions for ${resultName}`}
        aria-haspopup="menu"
        aria-expanded={menuAnchor ? true : undefined}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {plan.menu.map((action) => (
          <MenuItem
            key={action}
            sx={action === MarketplaceResultAction.RemoveFromNetwork ? { color: "error.main" } : undefined}
            onClick={() => {
              const trigger = menuAnchor;
              setMenuAnchor(null);
              if (trigger) perform(action, trigger);
            }}
          >
            {MARKETPLACE_RESULT_ACTION_LABELS[action]}
          </MenuItem>
        ))}
      </Menu>
      <Popover
        open={Boolean(invitationAnchor)}
        anchorEl={invitationAnchor}
        onClose={() => setInvitationAnchor(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
      >
        <NetworkInvitationForm
          message={invitationMessage}
          onMessageChange={setInvitationMessage}
          onCancel={() => setInvitationAnchor(null)}
          onSend={sendInvitation}
        />
      </Popover>
      {/* Mounted only while open: a page of results would otherwise hold a contacts query per card. */}
      {isSendingToContact && (
        <SendResultToContactDialog
          open
          onClose={() => setIsSendingToContact(false)}
          resultName={resultName}
          profileUrl={profileUrl}
        />
      )}
    </FooterRow>
  );
}

const ACTION_ICONS: Record<MarketplaceResultAction, React.ReactNode> = {
  [MarketplaceResultAction.AcceptInvitation]: <StarBorderIcon />,
  [MarketplaceResultAction.AddToNetwork]: <StarBorderIcon />,
  [MarketplaceResultAction.CancelInvitation]: <NotInterestedIcon />,
  [MarketplaceResultAction.CopyLink]: undefined,
  [MarketplaceResultAction.CreateProposal]: <AddBoxIcon />,
  [MarketplaceResultAction.RejectInvitation]: undefined,
  [MarketplaceResultAction.RemoveFromNetwork]: undefined,
  [MarketplaceResultAction.SendMessage]: <MessageIcon />,
  [MarketplaceResultAction.SendToContact]: undefined,
};

function isNetworkAction(action: MarketplaceResultAction): boolean {
  return (
    action === MarketplaceResultAction.AddToNetwork ||
    action === MarketplaceResultAction.AcceptInvitation ||
    action === MarketplaceResultAction.CancelInvitation
  );
}

/**
 * Status on the left, then the secondary, the primary and the ⋮ on the right. On a phone-width card
 * the primary takes a row of its own at the top, the secondary and the ⋮ share the next, and the
 * status goes last — the order the phone board draws them in, reached by `order` so the markup (and
 * the tab order) stays the same at every width.
 */
const FooterRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  minWidth: 0,
  "& > .status": {
    marginRight: "auto",
  },
  "& > .more": {
    flexShrink: 0,
  },
  [marketplaceResultIsPhone()]: {
    "& > .primary": { flexBasis: "100%", order: 1 },
    "& > .secondary": { flexGrow: 1, order: 2 },
    "& > .more": { marginLeft: "auto", order: 3 },
    "& > .status": { flexBasis: "100%", order: 4 },
  },
}));

const ConnectionStatus = styled("span")(({ theme }) => ({
  ...theme.typography.body2,
  alignItems: "center",
  color: theme.palette.text.secondary,
  display: "inline-flex",
  gap: theme.spacing(0.75),
}));
